import { format } from 'date-fns';
import React, {
  FC, memo, RefObject, useContext, useMemo, useRef,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { PresenterProps } from 'src/components/OperationDirectionsComponent';
import LicenseContext from 'src/contexts/LicenseContext';
import numberDecorator from 'src/decorators/number.decorator';
import { DirectionDisplaySettingEntity } from 'src/entities/DirectionDisplaySetting.entity';
import { DisplayOperationColumnEntity } from 'src/entities/DisplayOperationColumn.entity';
import { DisplayOperationRowEntity } from 'src/entities/DisplayOperationRow.entity';
import { Place, ShiftWithCycleModel } from 'src/models/ShiftModel';
import { DisplayOperationColumnKeyVo } from 'src/vo/DisplayOperationColumnKey.vo';

import {
  buildDirectionBlocks, DirectionBlock, PageMeta,
} from './pagination';
import style from './print.module.css';
import { useMeasuredPages } from './useMeasuredPages';

export type ActionKind = '積' | '降' | null;

type DeletedOrder = { toShift: ShiftWithCycleModel, deletedOperations: DisplayOperationRowEntity[] };

// A4 横（210mm 高）の px 換算。96dpi で 210mm ≈ 793px。
// 描画 DPI 依存のため、実機の改ページ位置を見て微調整する。
const PAGE_HEIGHT_PX = 793;
// フッタ（発行日時 + 通し番号）と余白の見込み高（px）。実機で微調整可。
const FOOTER_HEIGHT_PX = 60;
// .page の上下 padding（print.module.css の padding: 4mm → 上下計 8mm）の px 換算。
// 96dpi で 1mm ≈ 3.7795px。8mm ≈ 30px。実機の改ページ位置を見て微調整する。
const PAGE_VERTICAL_PADDING_PX = Math.round(8 * 3.7795);

function mergeBySameRow(
  input: DisplayOperationColumnEntity[][]
): DisplayOperationColumnEntity[][] {
  const groups = new Map<string, DisplayOperationColumnEntity[]>();

  input.forEach((row) => {
    // 一致判定するデータで識別キーを作る
    const identificationKey = row
      .filter((f) => f.key !== '数量' && f.key !== '合計重量' && f.key !== '合計体積' && f.key !== '到着予定時刻')
      .map((f) => `${f.key}:${f.value}:${f.klass}`)
      .join('|');

    // 識別キーでグループ化
    if (!groups.has(identificationKey)) {
      groups.set(
        identificationKey,
        row.map((f) => ({ ...f }))
      );
    } else {
      // 既存グループの数量・合計重量・合計体積を合算
      const existing = groups.get(identificationKey);
      if (existing) {
        row.reduce((_, thisField) => {
          const field = existing.find((f) => f.key === thisField.key);
          if (field && ['数量', '合計重量', '合計体積', '伝票数'].includes(thisField.key)) {
            field.value = String(parseFloat(field.value) + parseFloat(thisField.value));
          }
          return _;
        }, null);
      }
    }
  });

  // マップの値を配列にして返す
  return Array.from(groups.values());
}

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

const OperationDirectionPresenter: FC<PresenterProps> = memo((
  {
    shiftWithCycleModel,
    action,
    unit,
    operations,
    isYC,
    operationDiffs,
    shiftWithCycleModels,
    shiftIndex,
  }
) => {
  const displayVolume = unit !== '才';
  const displayUnit = unit !== '才' ? '重量' : '才数';
  const licenseContext = useContext(LicenseContext);
  const selectableDisplayDirectionConditionSettings = licenseContext?.selectableDisplayDirectionConditionSettings || [];
  let settings: DirectionDisplaySettingEntity[] = licenseContext?.displayDirectionConditions?.find((it) => it.name === 'default')?.settings || [];
  if (selectableDisplayDirectionConditionSettings.length > 0 && settings.length > 0) {
    const ids = selectableDisplayDirectionConditionSettings.map((it) => it.id);
    settings = settings.filter((it) => ids.includes(it.id));
  }

  const headers: string[] = settings.map((it: DirectionDisplaySettingEntity) => it.title);
  const act: ActionKind = action === '降' ? '降' : null;

  const propertyNamesThatCanBeGrouped = [
    '運転手名',
    '車両番号',
    '拠点名',
    '到着予定時刻',
    '出発予定時刻',
    '作業場所住所',
    '作業場所緯度',
    '作業場所経度',
  ];

  const propertyNamesThatCanBeSummed = [
    '合計重量',
    '合計体積',
    '数量'
  ];

  const propertyNamesThatCanBeSummedWithIndex = propertyNamesThatCanBeSummed.map((it) => {
    const idx = headers.indexOf(it);

    if (idx === -1) return null;

    return {
      idx,
      name: it
    };
  }).filter((maybe) => maybe)
    .sort((a, b) => a.idx - b.idx);

  const deletedOrders: DeletedOrder[] = [];
  const displayKyes = ['指定トラック', '作業場所', '作業場所住所', '合計重量', '合計体積', '数量', '備考'].concat(licenseContext.config?.custom_input_fields || []);
  if (isYC) {
    const operationDiff = operationDiffs[shiftWithCycleModel.driver.id];

    if (operationDiff?.deleted && operationDiff.deleted.length > 0) {
      operationDiff.deleted.forEach((it) => {
        const toShift = shiftWithCycleModels.find((r) => r.entity.id === it.to_shift_id);
        const operationIds = toShift.allPlaces('降').flatMap((place) => place.allOperations('降'))
                                    .filter((ops) => it.order_ids.includes(ops.getOrderID())).map((ops) => ops.getID());
        const deletedOperations = operations.filter((ops) => operationIds.includes(Number(ops.find((r) => r.key === 'id').value)));
        deletedOrders.push({ toShift, deletedOperations });
      });
    }
  }

  const isDNP = !!licenseContext?.config?.company_name?.match(/.*DNPロジスティクス.*/);
  // const isDNP = !!licenseContext?.config?.company_name?.match(/.*localhost.*/);
  const dnpSummary: Place[][] = [];
  if (isDNP) {
    let prevPlace: Place = null;
    let places: Place[] = [];
    let lastPlaceIndex = 0;
    shiftWithCycleModel.allPlaces(act).forEach((place, placeIndex) => {
      if (prevPlace !== null && ((prevPlace.isAction('積') && place.isAction('降')) || (prevPlace.isAction('降') && place.isAction('積')))) {
        dnpSummary[placeIndex - 1] = places;
        places = [];
      }
      places.push(place);
      prevPlace = place;
      lastPlaceIndex = placeIndex;
    });
    if (places.length > 0) {
      dnpSummary[lastPlaceIndex] = places;
    }
  }

  const shiftId = shiftWithCycleModel.entity.id;

  // 備考欄の有無（renderMemo の表示条件と一致）。memo を末尾ブロックとして計測・分割するために使う。
  const hasMemo = !!licenseContext.config?.operation_direction_memo;

  // operations は group をまたいで不変なので一度だけ index を作る（従来は group ごとに再生成していたが結果は同一）。
  const operationsById = useMemo(
    () => operations.reduce((acc, it) => {
      const id = it.find((r) => r.key === 'id').value;
      return acc.set(Number(id), it);
    }, new Map<number, DisplayOperationRowEntity>()),
    [operations]
  );

  // 実データ（回転グループ配列）。地点ブロック描画・計測の起点。
  const groups = useMemo(() => shiftWithCycleModel.allPlacesGrouped(act), [shiftWithCycleModel, act]);

  // 地点ごとの「表示行配列」（mergeBySameRow 後）を 1 回だけ計算して使い回すヘルパ。
  // (a) placeRowCounts（=配列長）, (b) 確定描画の run 描画, (c) 計測描画 で共通利用する。
  // place を key に lazy 充填する（同一 place インスタンスは同一行配列を返す）。
  // レンダ中の useRef 再代入はアンチパターンなので useMemo で空の Map を確保し、その中に
  // 充填する（依存が変わると新しい Map になりキャッシュは自然に無効化される）。
  const placeRowsCache = useMemo(
    () => new Map<Place, DisplayOperationColumnEntity[][]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftWithCycleModel, act, operationsById, headers],
  );
  const getPlaceRows = (place: Place): DisplayOperationColumnEntity[][] => {
    const cached = placeRowsCache.get(place);
    if (cached) return cached;

    const placeOperations = place.allOperations(act).sort((a, b) => a.compare(b));
    // operationsById の行は memo で共有されるため、chit を push する前に行を複製して
    // 複数フェーズ（計測→確定）の再描画で 伝票数 が二重加算されるのを防ぐ（現行の単一パス挙動を維持）。
    // id 不在でも空配列にフォールバックしてクラッシュを防ぐ。
    const chit = { key: '伝票数' as DisplayOperationColumnKeyVo, value: '1', klass: 'Integer' };
    const currentOperationAttributes = placeOperations.map((ops) => {
      const row = (operationsById.get(ops.getID()) || []).map((f) => ({ ...f }));
      row.push({ ...chit });
      return row;
    });

    const displayOperationAttributes: DisplayOperationColumnEntity[][] = currentOperationAttributes
      .map((currentOperationAttribute) => headers.map((header) => {
        const ret = currentOperationAttribute.find((r) => r.key === header);
        if (ret) { return ret; }

        return {
          key: header as DisplayOperationColumnKeyVo,
          klass: 'NilClass',
          value: null,
        };
      }));
    const merged = mergeBySameRow(displayOperationAttributes);
    placeRowsCache.set(place, merged);
    return merged;
  };

  // 地点 place に「数量/重量/体積の小計行」を出すか（現行 renderPlaceBlock の小計表示条件）。
  // 非 DNP のみ true になり得る。区切り行（空）/DNP 小計はこの述語の対象外。
  const placeHasSubtotal = (place: Place): boolean => {
    if (isDNP) return false;
    const rowCount = getPlaceRows(place).length;
    return place.allOperations(act).length > 1 && rowCount > 1 && !!propertyNamesThatCanBeSummedWithIndex.length;
  };

  // 地点 place が小計 tbody（placeSubtotal ブロック）を出力するか。
  // 現行 renderPlaceBlock は小計 tbody を必ず出していた:
  //   - 非 DNP: 数量小計（条件成立）か区切り行（空）のどちらかを常に出す → 常に true
  //   - DNP: dnpSummary[placeIndex] がある地点のみ DNP 小計を出す
  // この「常に小計 tbody が出る」挙動を維持するため、行レベルでも同条件で placeSubtotal を出す。
  const placeEmitsSubtotalBlock = (place: Place, placeIndex: number): boolean => {
    if (isDNP) return !!dnpSummary[placeIndex];
    return true;
  };

  // 列見出し <thead>。各ページ先頭に同一内容を描画する。
  // 計測フェーズでは theadRef を渡し、確定ページの thead 1 回分を available から差し引くために高さを測る。
  const renderThead = (theadRef?: RefObject<HTMLTableSectionElement>) => (
    <thead ref={theadRef} style={{ border: '1px solid #000' }} key={['OperationDirectionPresenter', 'headers', 'thead'].join('-')}>
      <tr key={['OperationDirectionPresenter', 'headers', 'tr'].join('-')}>
        {
          headers.map((header) => (
            <th
              key={
                [
                  'OperationDirectionPresenter',
                  'headers',
                  header
                ].join('-')
              }
            >
              {header}
            </th>
          ))
        }
      </tr>
    </thead>
  );

  // 回転数 > 1 のときの「<n回転目>」見出し。
  // ページ = 1 テーブルに複数 tbody の確定描画に合わせ、テーブル内の全幅行として描画する
  // （従来はテーブル外 <div> だったが、<table> 内に <div> は置けないため tbody 行に変更。見た目は同一）。
  const renderCycleHeading = (groupIndex: number) => (
    <tbody style={{ border: 'none' }} key={[shiftId, groupIndex, 'cycle-heading'].join('-')}>
      <tr style={{ border: 'none' }}>
        <td colSpan={headers.length} style={{ border: 'none', padding: '1mm 0mm' }}>
          <strong>
            {`<${groupIndex + 1}回転目>`}
          </strong>
        </td>
      </tr>
    </tbody>
  );

  // 地点明細セルの背景色（DNP は集荷行に斜線）。現行の式を流用。
  const detailRowBgColor = (row: DisplayOperationColumnEntity[]): string => {
    if (!isDNP) return '#fff';
    return row.find((el) => el.key === '作業')?.value === '集荷'
      ? 'repeating-linear-gradient(45deg, #aaa, #aaa 1px,#fff 0px, #fff 19px)'
      : '#fff';
  };

  // 地点明細の 1 行（<tr>）を描画する。
  // グループ列（propertyNamesThatCanBeGrouped）は run（同一地点の連続行）の先頭でのみ
  // rowSpan=runLength で出し、それ以外の行では当該セルを出さない（現行 renderPlaceBlock の
  // index===0 ロジックを「run 内インデックス」に置き換えただけ）。
  // 計測フェーズは各行を独立 <tr>（runIndex=0, runLength=1）で描画して行ごとに高さを確定する。
  const renderPlaceDetailTr = (
    place: Place,
    placeIndex: number,
    row: DisplayOperationColumnEntity[],
    rowKey: number,
    runIndex: number,
    runLength: number,
    trRef?: (el: HTMLTableRowElement | null) => void,
  ) => (
    <tr
      key={[shiftId, placeIndex, place.latlng, rowKey, 'tr'].join('-')}
      style={{ background: detailRowBgColor(row) }}
      ref={trRef}
    >
      {row.map((attr) => (
        ((runIndex === 0 || !propertyNamesThatCanBeGrouped.includes(attr.key)) && (
          <td
            key={[shiftId, placeIndex, place.latlng, rowKey, attr.key].join('-')}
            rowSpan={runIndex === 0 && propertyNamesThatCanBeGrouped.includes(attr.key) ? runLength : 1}
            style={{ textAlign: ['Integer', 'Float'].includes(attr.klass) ? 'right' : 'left' }}
          >
            {
              (() => {
                if (attr.key === '到着予定時刻') {
                  return format(place.arrivalAt(), 'HH:mm');
                }
                if (['Integer', 'Float'].includes(attr.klass)) {
                  return Number(attr.value).toLocaleString();
                }
                return attr.value;
              })()
            }
          </td>
        ))
      ))}
    </tr>
  );

  // 地点小計の <tr>（中身のみ）。現行 renderPlaceBlock の小計 3 分岐をそのまま流用する。
  // - 非 DNP かつ小計条件成立: 数量/重量/体積の小計行
  // - 非 DNP かつ条件不成立: 区切り行（空 colSpan）
  // - DNP かつ dnpSummary[placeIndex] あり: DNP 小計行
  const renderPlaceSubtotalTr = (place: Place, placeIndex: number) => {
    const numericSubtotal = placeHasSubtotal(place);
    if (!isDNP && numericSubtotal) {
      return (
        <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
          {
            headers.map((header, index) => {
              const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
              const isLast = index === headers.length - 1;
              const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
                .includes(index) || isLast || sumCurrent;
              const sumValue = () => {
                if (header === '合計重量') return numberDecorator.round(place.itemWeightKg(act), 2);
                if (header === '数量') return place.itemCount(act);
                if (header === '合計体積' && place.itemVolumeM3(act) > 0) return numberDecorator.round(place.itemVolumeM3(act), 2);

                return '';
              };
              return (
                <td
                  key={[placeIndex, place.latlng, index, header, 'sum'].join('-')}
                  style={{
                    borderRightWidth: hasBorder ? '1px' : '0',
                    textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left'
                  }}
                >
                  {
                    (() => {
                      if (index === 0 && sumValue() === '') {
                        return '小計';
                      }
                      if (['数量', '合計重量', '合計体積'].includes(header)) {
                        return sumValue().toLocaleString();
                      }
                      return sumValue();
                    })()
                  }
                </td>
              );
            })
          }
        </tr>
      );
    }
    if (!isDNP && !numericSubtotal) {
      return (
        <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
          <td aria-label="区切り行" colSpan={headers.length} className="text-center" key={[shiftId, placeIndex, place.latlng, 'subtotal', 'td'].join('-')}> </td>
        </tr>
      );
    }
    // isDNP && dnpSummary[placeIndex] あり（hasSubtotal でこの分岐のみ到達）
    return (
      <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
        {
          headers.map((header, index) => {
            const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
            const isLast = index === headers.length - 1;
            const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
              .includes(index) || isLast || sumCurrent;
            const sumValue = () => {
              const sumPlaces = dnpSummary[placeIndex];
              if (header === '合計重量') {
                return numberDecorator.round(sumPlaces.map((it) => it.itemWeightKg(act)).reduce((prev, curr) => prev + curr), 2);
              }
              if (header === '数量') {
                return sumPlaces.map((it) => it.itemCount(act)).reduce((prev, curr) => prev + curr);
              }
              if (header === '合計体積' && sumPlaces.some((it) => it.itemVolumeM3(act) > 0)) {
                return numberDecorator.round(sumPlaces.map((it) => it.itemVolumeM3(act) || 0).reduce((prev, curr) => prev + curr), 2);
              }

              return '';
            };
            return (
              <td
                key={[placeIndex, place.latlng, index, header, 'sum'].join('-')}
                style={{
                  borderRightWidth: hasBorder ? '1px' : '0',
                  textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left'
                }}
              >
                {
                  (() => {
                    if (index === 0 && sumValue() === '') {
                      return '小計';
                    }
                    if (['数量', '合計重量', '合計体積'].includes(header)) {
                      return sumValue().toLocaleString();
                    }
                    return sumValue();
                  })()
                }
              </td>
            );
          })
        }
      </tr>
    );
  };

  // グループ（回転）合計の <tr>（中身のみ。tbody は呼び出し側でラップする）。
  const renderGroupTotalRow = (group: Place[]) => {
    if (!propertyNamesThatCanBeSummedWithIndex.length) return null;
    return (
      <tr className={style.summery} key={[shiftId, 'total', 'tr'].join('-')}>
          {
            headers.map((header, index) => {
              const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
              const isLast = index === headers.length - 1;
              const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
                .includes(index) || isLast || sumCurrent;

              const sumValue = () => {
                const totalWeight = group.reduce((acc, it) => acc + it.itemWeightKg(action === '降' ? '降' : '積'), 0);
                const totalItemCount = group.reduce((acc, it) => acc + it.itemCount(action === '降' ? '降' : '積'), 0);
                const totalVolume = group.reduce((acc, it) => acc + it.itemVolumeM3(action === '降' ? '降' : '積'), 0);
                if (header === '合計重量') return totalWeight.toLocaleString();
                if (header === '数量') return totalItemCount.toLocaleString();
                if (header === '合計体積' && totalVolume > 0) return totalVolume.toLocaleString();

                return '';
              };

              return (
                <td
                  key={
                    [shiftId, 'sum', header, index].join('-')
                  }
                  style={{
                    borderRightWidth: hasBorder ? '1px' : '0',
                    textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left',
                  }}
                >
                  {index === 0 && sumValue() === '' ? '合計' : sumValue()}
                </td>
              );
            })
          }
      </tr>
    );
  };

  // YC 削除分の行群（中身のみ。tbody は呼び出し側でラップする）。
  const renderDeletedRows = (it: DeletedOrder) => (
    <>
      <tr style={{ borderLeft: 'none' }}>
        <td colSpan={headers.length} style={{ borderLeft: '0px none #fff', borderRight: 'none' }}>
          <h3>{`${shiftWithCycleModel.truck.licensePlateValue} → ${it.toShift.truck.licensePlateValue}`}</h3>
        </td>
      </tr>
      {it.deletedOperations.map((ops) => (
        <tr style={{ border: '1px solid #000' }} key={[shiftId, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, 'tr'].join('-')}>
          {
            headers.map((header) => {
              const attr = ops.find((r) => r.key === header);
              const value = displayKyes.includes(header) ? attr.value : '';
              return (
                <td key={[shiftId, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, header].join('-')}>{value}</td>
              );
            })
          }
        </tr>
      ))}
    </>
  );

  // タイトル行 ＋ フルサマリヘッダー。各ページ先頭に同一内容を描画する（§4.4）。
  // pageMeta があれば X / Y を併記する。
  const renderCourseHeader = (pageMeta?: PageMeta) => (
    <>
      <div className={style.line}>
        <span className={style.left}>
          <h1>
            {shiftWithCycleModel.entity.course_number != null && `コース：${shiftWithCycleModel.entity.course_number}`}
          </h1>
        </span>
        <span className={style.center}><h1>運行指示書</h1></span>
        {pageMeta && (
          <span className={style.pageNumber}>
            {`${pageMeta.pageNumber} / ${pageMeta.totalPages}`}
          </span>
        )}
      </div>
      <div className={style.flex}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', gap: '20px' }}>
            <table style={{ height: '50%', width: 'fit-content', }}>
              <tbody>
                <tr>
                  <th style={{ minWidth: '80px' }}>運行日</th>
                  <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>
                    {
                      format(shiftWithCycleModel.startAt(), 'yyyy-MM-dd') === format(shiftWithCycleModel.endAt(), 'yyyy-MM-dd')
                        ? format(shiftWithCycleModel.startAt(), 'yyyy/MM/dd')
                          : `${format(shiftWithCycleModel.startAt(), 'yyyy/MM/dd')} - ${format(shiftWithCycleModel.endAt(), 'yyyy/MM/dd')}`
                    }
                  </td>
                </tr>
                <tr>
                  <th>伝票数</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{new Set(shiftWithCycleModel.orders.map((order) => order.code)).size}</td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: 'fit-content' }}>
              <tbody>
                <tr>
                  <th style={{ minWidth: '100px' }}>運送会社</th>
                  <td style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>{shiftWithCycleModel.entity.orders[0]?.order_assignment_attributes.carrier_company}</td>
                </tr>
                <tr>
                  <th>車番</th>
                  <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.licensePlateValueWithKlassAndTruckNumber()}</td>
                </tr>
                <tr>
                  <th>ドライバー</th>
                  <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.driverName()}</td>
                </tr>
                <tr>
                  <th>電話番号</th>
                  <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.driverPhoneNumber() || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '20px' }}>
            <table style={{ width: 'fit-content', }}>
              <tbody>
                <tr>
                  <th style={{ minWidth: '80px' }}>納品先数</th>
                  <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>{shiftWithCycleModel.placeCount('降')}</td>
                </tr>
                <tr>
                  <th>総数量</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.itemCount('降')}</td>
                </tr>
                <tr>
                  <th>{`総${displayUnit}`}</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{numberDecorator.round(shiftWithCycleModel.totalWeightKg('降'), 2)}</td>
                </tr>
                <tr>
                  <th>総体積</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{displayVolume && (numberDecorator.round(shiftWithCycleModel.totalVolume('降'), 2))}</td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: 'fit-content', }}>
              <tbody>
                <tr>
                  <th style={{ minWidth: '80px' }}>重量積載率</th>
                  <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>{shiftWithCycleModel.loadingWeightRateTimelineText()}</td>
                </tr>
                <tr>
                  <th>体積積載率</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.loadingVolumeRateTimelineText()}</td>
                </tr>
                <tr>
                  <th>回転数</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.cycleCountText()}</td>
                </tr>
                <tr>
                  <th>総走行距離</th>
                  <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>
                    {shiftWithCycleModel.totalDistanceRoundKm()}
                    km
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );

  // 備考欄。
  const renderMemo = () => (
    licenseContext.config?.operation_direction_memo ? (
      <div className={style.memo}>
        {
          (licenseContext.config?.operation_direction_memo || '').split('\n').map((it, index) => (
            index === 0
            ? (
              <div key={['operation_direction_memo', index].join('-')}>
                <strong>備考欄：</strong>
                {it}
              </div>
            )
            : <div key={['operation_direction_memo', index].join('-')}>{it}</div>
          ))
        }
      </div>
    ) : null
  );

  // フッタ（発行日時 ＋ 通し番号）。
  const renderFooter = () => (
    <div className={style.footer}>
      {`${formatDateTime(new Date())} 発行 `}
      {`<${shiftIndex + 1}/${shiftWithCycleModels.length}>`}
    </div>
  );

  // 行レベルのブロック列。placeRow / placeSubtotal で地点を分解し、行単位で改ページ可能にする。
  const blocks = useMemo<DirectionBlock[]>(() => buildDirectionBlocks({
    placeRowCounts: groups.map((g) => g.map((place) => getPlaceRows(place).length)),
    hasSubtotal: groups.map((g) => g.map((place, placeIndex) => placeEmitsSubtotalBlock(place, placeIndex))),
    showCycleHeading: shiftWithCycleModel.operationCycles.length > 1,
    showGroupTotal: !!propertyNamesThatCanBeSummedWithIndex.length,
    deletedCount: isYC ? deletedOrders.length : 0,
    hasMemo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [groups, shiftWithCycleModel.operationCycles.length, propertyNamesThatCanBeSummedWithIndex.length, isYC, deletedOrders.length, hasMemo]);

  // cycleHeading ブロックは次ブロックと同ページに留める（ページ末尾に見出しが孤立しないように）。
  const keepWithNext = useMemo<boolean[]>(
    () => blocks.map((b) => b.kind === 'cycleHeading'),
    [blocks],
  );

  // 計測フェーズ用: 1 ブロックを measurable な単一 DOM 要素として描画する。
  // placeRow=独立 <tr>（rowSpan なし）、placeSubtotal=小計 <tr>、cycleHeading/groupTotal=全幅 <tr>、deleted=<tbody>。
  // <tr> 系は ref を付けるため個別 <tbody> でラップする（multiple tbody は valid）。
  const renderMeasureBlock = (b: DirectionBlock, i: number) => {
    const setRef = (el: HTMLElement | null) => { blockRefs.current[i] = el; };
    if (b.kind === 'cycleHeading') {
      return (
        <tbody key={['measure', i].join('-')} style={{ border: 'none' }}>
          <tr ref={setRef} style={{ border: 'none' }}>
            <td colSpan={headers.length} style={{ border: 'none', padding: '1mm 0mm' }}>
              <strong>{`<${b.groupIndex + 1}回転目>`}</strong>
            </td>
          </tr>
        </tbody>
      );
    }
    if (b.kind === 'placeRow') {
      const place = groups[b.groupIndex][b.placeIndex];
      const row = getPlaceRows(place)[b.rowIndex];
      return (
        <tbody key={['measure', i].join('-')} style={{ border: '1px solid #000' }}>
          {/* 計測は各行独立（runIndex=0, runLength=1）。グループ列も rowSpan=1 で出して行高を確定する。 */}
          {renderPlaceDetailTr(place, b.placeIndex, row, b.rowIndex, 0, 1, setRef)}
        </tbody>
      );
    }
    if (b.kind === 'placeSubtotal') {
      const place = groups[b.groupIndex][b.placeIndex];
      return (
        <tbody key={['measure', i].join('-')} style={{ border: '1px solid #000' }} ref={setRef}>
          {renderPlaceSubtotalTr(place, b.placeIndex)}
        </tbody>
      );
    }
    if (b.kind === 'groupTotal') {
      return (
        <tbody key={['measure', i].join('-')} ref={setRef}>
          {renderGroupTotalRow(groups[b.groupIndex])}
        </tbody>
      );
    }
    if (b.kind === 'memo') {
      // 備考欄を末尾ブロックとして計測する。確定描画と同じ memo 実体（renderMemo）を
      // 全幅セル内に置き、その高さを測る（<table> 内なので tbody/tr/td でラップする）。
      return (
        <tbody key={['measure', i].join('-')} style={{ border: 'none' }}>
          <tr ref={setRef} style={{ border: 'none' }}>
            <td colSpan={headers.length} style={{ border: 'none' }}>
              {renderMemo()}
            </td>
          </tr>
        </tbody>
      );
    }
    // deleted は tbody そのものに ref を付ける。
    return (
      <tbody key={['measure', i].join('-')} ref={setRef} style={{ borderLeft: 'none' }}>
        {renderDeletedRows(deletedOrders[b.deletedIndex])}
      </tbody>
    );
  };

  // 確定フェーズ用: 1 ページ分のブロック列を描画する。
  // 連続する同一 (groupIndex, placeIndex) の placeRow run を 1 つの <tbody> にまとめ、
  // グループ列は run 先頭行に rowSpan=run長 で 1 回だけ出す（ページをまたぐと各ページ先頭で出し直す）。
  const renderPageBlocks = (pm: PageMeta) => {
    const elements: React.ReactNode[] = [];
    const indices = pm.blockIndices;
    let i = 0;
    while (i < indices.length) {
      const b = blocks[indices[i]];
      if (b.kind === 'placeRow') {
        // 同一 (g, p) の連続 placeRow をまとめる。
        const runStart = i;
        const { groupIndex, placeIndex } = b;
        const runRows: { kind: 'placeRow'; groupIndex: number; placeIndex: number; rowIndex: number }[] = [];
        while (i < indices.length) {
          const cur = blocks[indices[i]];
          if (cur.kind !== 'placeRow' || cur.groupIndex !== groupIndex || cur.placeIndex !== placeIndex) break;
          runRows.push(cur);
          i += 1;
        }
        const place = groups[groupIndex][placeIndex];
        const rows = getPlaceRows(place);
        const runLength = runRows.length;
        elements.push(
          <tbody
            style={{ border: '1px solid #000' }}
            key={[shiftId, 'run', pm.pageNumber, runStart].join('-')}
          >
            {runRows.map((rr, runIndex) => renderPlaceDetailTr(
              place, placeIndex, rows[rr.rowIndex], rr.rowIndex, runIndex, runLength,
            ))}
          </tbody>
        );
        continue;
      }
      if (b.kind === 'cycleHeading') {
        elements.push(renderCycleHeading(b.groupIndex));
      } else if (b.kind === 'placeSubtotal') {
        const place = groups[b.groupIndex][b.placeIndex];
        elements.push(
          <tbody
            style={{ border: '1px solid #000' }}
            key={[shiftId, b.placeIndex, place.latlng, 'subtotal'].join('-')}
          >
            {renderPlaceSubtotalTr(place, b.placeIndex)}
          </tbody>
        );
      } else if (b.kind === 'groupTotal') {
        elements.push(
          <tbody style={{ border: '1px solid #000' }} key={[shiftId, b.groupIndex, 'total', 'tbody'].join('-')}>
            {renderGroupTotalRow(groups[b.groupIndex])}
          </tbody>
        );
      } else if (b.kind === 'deleted') {
        elements.push(
          <tbody style={{ borderLeft: 'none' }} key={[shiftId, deletedOrders[b.deletedIndex].toShift.entity.id, 'deleted', 'tbody'].join('-')}>
            {renderDeletedRows(deletedOrders[b.deletedIndex])}
          </tbody>
        );
      }
      // b.kind === 'memo' はテーブル後に描画するためここでは何も積まない。
      i += 1;
    }
    return elements;
  };

  // ページに memo ブロックが載っているか（確定描画でテーブル後に renderMemo を出すか）。
  const pageHasMemo = (pm: PageMeta): boolean => pm.blockIndices.some((idx) => blocks[idx].kind === 'memo');

  // 計測（オフスクリーン）用 ref。
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // 列見出し(thead)の高さ計測用 ref。計測テーブルは auto 列幅整合のため thead を含むが、
  // 確定ページでも thead は 1 回出るため、ブロック高さ算定では thead を含めない（行 <tr>/<tbody> を直接測る）。
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const blockRefs = useRef<Array<HTMLElement | null>>([]);

  const pages = useMeasuredPages({
    blockCount: blocks.length,
    containerRef,
    blockRefs,
    headerRef,
    theadRef,
    footerHeight: FOOTER_HEIGHT_PX,
    pageHeightPx: PAGE_HEIGHT_PX,
    pageVerticalPaddingPx: PAGE_VERTICAL_PADDING_PX,
    depsKey: shiftId,
    keepWithNext,
  });

  return (
    <>
      <Helmet>
        <title>配送指示書</title>
      </Helmet>

      {/* 計測フェーズ: 1 つのテーブルに thead ＋ 全ブロックを「行」として描画し、各ブロックの実高さを測る。
          auto 列幅を全行共通の文脈で測れるよう 1 テーブルにまとめる。rowSpan は使わず各行独立で測る。 */}
      {pages === null && (
        <div
          ref={containerRef}
          aria-hidden
          className={style.page}
          style={{
            position: 'absolute', visibility: 'hidden', left: -99999, top: 0, width: '297mm',
          }}
        >
          <div ref={headerRef}>{renderCourseHeader()}</div>
          <table className={style.operationTable} style={{ width: '100%', margin: '0mm' }}>
            {renderThead(theadRef)}
            {blocks.map((b, i) => renderMeasureBlock(b, i))}
          </table>
        </div>
      )}

      {/* 確定フェーズ: ページ毎に [フルサマリヘッダー ＋ X/Y] ＋ 1 テーブル（thead ＋ ブロック群）。 */}
      {pages !== null && pages.map((pm) => (
        <div className={style.page} style={{ page: 'A4OperationDirection' }} key={['page', shiftId, pm.pageNumber].join('-')}>
          {renderCourseHeader(pm)}
          <div style={{ margin: '2mm 0mm 0mm 0mm' }} />
          <table className={style.operationTable} style={{ width: '100%', margin: '0mm' }} key={['OperationDirectionPresenter', 'table', pm.pageNumber].join('-')}>
            {renderThead()}
            {renderPageBlocks(pm)}
          </table>
          {pageHasMemo(pm) && renderMemo()}
          {renderFooter()}
        </div>
      ))}
    </>
  );
});

export default OperationDirectionPresenter;
